import { GetServerSidePropsContext } from 'next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useEffect } from 'react';
import { store, StoreContext } from 'stores/store';
import ConnectWrapper from 'templates/Connect/ConnectWrapper';
import { IAuthDetails, verifyAuth } from 'utils/authUtils';
import { getConfigEntry } from 'utils/contentClient';
import { IResourceData, ISponsorData } from 'utils/globalTypes';
import UseSsrAuth from 'utils/hooks/useSsrAuth';
import { useUserAuth } from 'utils/hooks/useUserAuth';
import { getObjProperty } from 'utils/object';
import { FORGOT_PASSWORD, SIGN_IN, SIGN_UP } from 'utils/urlUtils';

const seoMapper = {
  [SIGN_IN]: {
    title: 'USREM | Sign In',
    description:
      'Welcome back to USREM Marketplace. Please sign in to buy and sell fractions of residential and commercial real estate properties.'
  },
  [SIGN_UP]: {
    title: 'USREM | Sign Up',
    description:
      'USREM’s marketplace enables you to buy and sell fractions of residential and commercial real estate properties, with the sponsors’ blessing'
  },
  [FORGOT_PASSWORD]: {
    title: 'USREM | Reset Password',
    description: "Welcome back to USREM Marketplace. Let's get you trading again."
  }
};

const { publicRuntimeConfig } = getConfig();
const { NEXT_PUBLIC_DOMAIN } = publicRuntimeConfig;

function ConnectPage({
  images,
  partners,
  sponsors,
  state,
  authDetails
}: {
  images?: IResourceData[];
  partners?: IResourceData[];
  sponsors?: ISponsorData[];
  state?: string;
  authDetails: IAuthDetails;
}) {
  const {
    query: { all }
  } = useRouter();
  const url = `https://${NEXT_PUBLIC_DOMAIN}/connect`;

  const clientAuthDetails = useUserAuth();

  const getSeoConfig = () => {
    if (!Array.isArray(all)) return;
    return seoMapper[all[0]];
  };

  useEffect(() => {
    verifyAuth(authDetails, clientAuthDetails);
  }, [authDetails, clientAuthDetails]);

  return (
    <>
      <NextSeo canonical={url} {...getSeoConfig()} />
      <StoreContext.Provider value={{ ...store }}>
        <ConnectWrapper
          images={images}
          partners={partners}
          sponsors={sponsors}
          defaultState={state}
        />
      </StoreContext.Provider>
    </>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const state = (ctx?.params?.all && ctx?.params?.all[0]) || ctx?.query?.s || '';

  const authProps = await UseSsrAuth(ctx);
  const content = await getConfigEntry('pages/connect');
  const connectImages = getObjProperty(content as Record<string, unknown>, 'gallery') || [];
  const partnerImages = getObjProperty(content as Record<string, unknown>, 'partners') || [];
  const sponsors = getObjProperty(content as Record<string, unknown>, 'sponsors') || [];

  return {
    props: {
      ...authProps.props,
      state,
      images: connectImages ?? null,
      partners: partnerImages ?? null,
      sponsors: sponsors ?? null
    }
  };
}

export default ConnectPage;
